import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Phone, Clock, ArrowRight, ArrowLeft, CheckCircle2, XCircle, Info, MessageCircle } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import CopyablePhone from './CopyablePhone';

const CallTracePanel = ({ phones = [], isAppendix = false, onOpenWhatsAppConversation }) => {
    const { authFetch } = useAuth();
    const [calls, setCalls] = useState([]);
    const [waConversations, setWaConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hoveredCallId, setHoveredCallId] = useState(null);

    const phonesStr = (phones || []).join(',');
    const normalizedPhones = useMemo(() => {
        return [...new Set(phonesStr.split(',').filter(p => !!p).map(p => {
            let n = String(p).trim().replace(/\s/g, '').replace(/-/g, '');
            if (n.startsWith('+34')) n = n.slice(3);
            if (n.startsWith('0034')) n = n.slice(4);
            return n;
        }))];
    }, [phonesStr]);

    useEffect(() => {
        if (normalizedPhones.length === 0) {
            setCalls([]);
            return;
        }

        const fetchAllTraces = async () => {
            setLoading(true);
            try {
                const callsRes = await authFetch('/api/calls/by-phones', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phones: normalizedPhones })
                });

                let callsData = [];
                if (callsRes.ok) {
                    const data = await callsRes.json();
                    callsData = data.calls || [];
                }

                let waData = [];
                if (normalizedPhones.length > 0) {
                    const waRes = await authFetch(`/api/whatsapp/conversations/${normalizedPhones[0]}`);
                    if (waRes.ok) {
                        waData = await waRes.json();
                    }
                }

                setCalls(callsData);
                setWaConversations(waData || []);

            } catch (err) {
                console.error("Error fetching call trace:", err);
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(fetchAllTraces, 800);
        return () => clearTimeout(timer);
    }, [normalizedPhones, authFetch]);

    const instanceId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
    useEffect(() => {
        if (normalizedPhones.length === 0) return;

        const waChannelName = `wa-trace-${normalizedPhones.join('-')}-${instanceId}`;
        const callsChannelName = `calls-trace-${normalizedPhones.join('-')}-${instanceId}`;

        const waChannel = supabase
            .channel(waChannelName)
            .on('postgres_changes', { event: 'INSERT', schema: 'messaging', table: 'conversations' }, (payload) => {
                const newConv = payload.new;
                if (normalizedPhones.includes(newConv.phone_number)) {
                    setWaConversations(prev => [newConv, ...prev.filter(c => c.conversation_id !== newConv.conversation_id)]);
                }
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'messaging', table: 'conversations' }, (payload) => {
                const updated = payload.new;
                if (normalizedPhones.includes(updated.phone_number)) {
                    setWaConversations(prev => prev.map(c => c.conversation_id === updated.conversation_id ? { ...c, ...updated } : c));
                }
            })
            .subscribe();

        const callsChannel = supabase
            .channel(callsChannelName)
            .on('postgres_changes', { event: 'INSERT', schema: 'espuma', table: 'calls' }, (payload) => {
                const newCall = payload.new;
                const matches = normalizedPhones.includes(newCall.caller_phone) || normalizedPhones.includes(newCall.callee_phone);
                if (matches) {
                    setCalls(prev => [newCall, ...prev.filter(c => c.id !== newCall.id)]);
                }
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'espuma', table: 'calls' }, (payload) => {
                const updated = payload.new;
                const matches = normalizedPhones.includes(updated.caller_phone) || normalizedPhones.includes(updated.callee_phone);
                if (matches) {
                    setCalls(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
                }
            })
            .subscribe();

        return () => { 
            supabase.removeChannel(waChannel);
            supabase.removeChannel(callsChannel);
        };
    }, [normalizedPhones, instanceId]);

    const timelineItems = useMemo(() => {
        const mappedCalls = calls.map(c => ({ ...c, _type: 'call', _date: new Date(c.start_time) }));
        const mappedWa = waConversations.map(c => ({ ...c, _type: 'wa', _date: new Date(c.created_at) }));
        return [...mappedCalls, ...mappedWa].sort((a, b) => b._date - a._date);
    }, [calls, waConversations]);

    return (
        <div className="w-full h-full flex flex-col bg-slate-900 overflow-hidden">
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                </div>
            ) : timelineItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-center px-6">
                    <Info size={28} className="opacity-20 mb-3" />
                    <p className="text-xs font-medium">Sin historial disponible.</p>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-800">
                    {timelineItems.map((item, idx) => {
                        const isWa = item._type === 'wa';
                        const isIncoming = !isWa && item.direction === 'INBOUND';
                        const isSale = !isWa && item.is_sale;
                        const dateObj = isWa ? new Date(item.created_at) : new Date(item.start_time);
                        const id = isWa ? item.conversation_id : item.id;

                        return (
                            <div 
                                key={`${item._type}-${id}-${idx}`}
                                className={`relative flex items-center gap-4 px-4 py-3 hover:bg-slate-800/50 transition-colors ${idx === 0 ? 'cursor-default' : 'cursor-pointer'} group`}
                                onMouseEnter={() => !isWa && setHoveredCallId(id)}
                                onMouseLeave={() => setHoveredCallId(null)}
                                onClick={() => idx !== 0 && isWa && onOpenWhatsAppConversation && onOpenWhatsAppConversation(item.conversation_id)}
                            >
                                {/* Icon Column */}
                                <div className={`p-2 rounded-lg flex-shrink-0 ${
                                    isWa ? 'bg-emerald-500/10 text-emerald-500' : 
                                    isIncoming ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'
                                }`}>
                                    {isWa ? <MessageCircle size={16} /> : (isIncoming ? <ArrowLeft size={16} /> : <ArrowRight size={16} />)}
                                </div>

                                {/* Info Column */}
                                <div className="flex-1 min-w-0 pr-4">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className={`text-[11px] font-bold uppercase tracking-wider ${
                                            isWa ? 'text-emerald-400' : isIncoming ? 'text-blue-400' : 'text-purple-400'
                                        }`}>
                                            {isWa ? `WhatsApp (${item.status === 'open' ? 'Abierto' : 'Cerrado'})` : 
                                             isIncoming ? 'Llamada Entrante' : 'Llamada Saliente'}
                                        </span>
                                        {isSale && (
                                            <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-black border border-emerald-500/30">SALE</span>
                                        )}
                                        {!isWa && item.status === 'CONNECTED' && !isSale && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        )}
                                        {!isWa && item.status !== 'CONNECTED' && (
                                            <XCircle size={12} className="text-slate-600" />
                                        )}
                                    </div>
                                    <CopyablePhone
                                        phone={isWa ? item.phone_number : (isIncoming ? item.caller_phone : item.callee_phone)}
                                        className="w-full"
                                        textClassName="text-xs text-slate-300 font-medium"
                                    >
                                        {isWa ? 'WhatsApp' : 'Desconocido'}
                                    </CopyablePhone>
                                </div>

                                {/* Date/Time Column */}
                                <div className="text-right flex-shrink-0">
                                    <p className="text-[10px] font-bold text-slate-200">
                                        {dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                                    </p>
                                    <div className="flex items-center justify-end gap-1 text-[9px] text-slate-500 font-mono">
                                        <Clock size={8} />
                                        <span>{dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>

                                {/* Floating Summary Tooltip */}
                                {!isWa && item.summary && hoveredCallId === id && (
                                    <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-64 p-3 bg-slate-950 border border-slate-700 rounded-xl shadow-2xl z-[100] animate-in fade-in zoom-in duration-200 pointer-events-none">
                                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5 border-b border-slate-800 pb-1">
                                            <Info size={10} /> Resumen de llamada
                                        </h4>
                                        <p className="text-[11px] text-slate-300 leading-relaxed italic line-clamp-6">
                                            "{item.summary}"
                                        </p>
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-700"></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CallTracePanel;

